<script setup lang="ts">
type FurtherIcon = "key-round" | "sparkles" | "map-pin" | "headphones" | "trees";

interface FurtherItem {
  title: string;
  description: string;
  href: string;
  icon?: FurtherIcon;
}

interface FurtherBlockData {
  eyebrow?: string;
  header: string;
  intro?: string;
  items: FurtherItem[];
}

defineProps<{
  block: FurtherBlockData;
}>();

const localePath = useLocalePath();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    aria-labelledby="about-further-heading"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-5xl px-4 pt-2 pb-8 sm:px-6 md:px-8 md:pt-4 md:pb-12">
      <!-- Section header -->
      <header class="mb-8 max-w-3xl md:mb-10">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          id="about-further-heading"
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <!-- Editorial "further reading" list — each row is a full-width link.
           SEO: anchor text = Playfair italic title + keyword-rich description.
           Internal linking from /about lifts target pages in SERP. -->
      <nav :aria-label="block.header">
        <ul
          class="overflow-hidden rounded-2xl border border-slate-200/80 bg-white/70 shadow-sm backdrop-blur-sm dark:border-slate-700/60 dark:bg-slate-800/50"
        >
          <li
            v-for="(item, i) in block.items"
            :key="item.href"
            :class="i > 0 ? 'border-t border-slate-200/80 dark:border-slate-700/60' : ''"
          >
            <NuxtLink
              :to="localePath(item.href)"
              class="group flex items-start gap-4 px-5 py-5 transition-colors duration-200 hover:bg-amber-50/60 focus-visible:outline-none focus-visible:bg-amber-50/60 focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-inset sm:gap-6 sm:px-7 sm:py-7 md:px-8 lg:px-10 dark:hover:bg-amber-500/5 dark:focus-visible:bg-amber-500/5"
            >
              <span
                v-if="item.icon"
                class="flex size-11 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 transition-transform duration-300 group-hover:scale-110 sm:size-12 dark:bg-amber-500/10 dark:text-amber-300"
              >
                <Icon :name="`lucide:${item.icon}`" class="size-5 sm:size-6" aria-hidden="true" />
              </span>

              <div class="min-w-0 flex-1">
                <h3
                  class="font-display text-lg italic leading-snug text-slate-900 transition-colors group-hover:text-amber-800 sm:text-xl md:text-2xl break-words dark:text-white dark:group-hover:text-amber-200"
                >
                  {{ item.title }}
                </h3>
                <p
                  class="mt-1 font-body text-sm leading-relaxed text-slate-600 sm:text-base dark:text-slate-400"
                >
                  {{ item.description }}
                </p>
              </div>

              <Icon
                name="lucide:arrow-up-right"
                class="mt-2 size-5 shrink-0 text-slate-400 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-amber-600 sm:size-6 dark:text-slate-500 dark:group-hover:text-amber-300"
                aria-hidden="true"
              />
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </div>
  </section>
</template>
